import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { TrendingDown, TrendingUp } from 'lucide-react';
import RealTimePrice from '@/components/shared/real-time-price/RealTimePrice';
import { formatAmount } from '@/utils/formatters';
import { useCrmCalculateTrade } from '@/api/instruments/use-crm-calculate';
import { useCrmAllPrices } from '@/hooks/useCrmAllPrices';
import { Trade } from '@/models/trade';

interface EditLotEntryDialogProps {
    trade: Trade | null;
    open: boolean;
    onClose: () => void;
    onSave: (data: { lot_size: number; price: number }) => Promise<void>;
}

const MIN_LOT = 0.01;
const MAX_LOT = 100;

export const EditLotEntryDialog: React.FC<EditLotEntryDialogProps> = ({
    trade,
    open,
    onClose,
    onSave,
}) => {
    const [lotSize, setLotSize] = useState('');
    const [entryPrice, setEntryPrice] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const prevValuesRef = useRef<{
        lotSize: string;
        entryPrice: string;
        currentPrice: number;
    } | null>(null);

    const calculateMutation = useCrmCalculateTrade();
    const { prices } = useCrmAllPrices(trade?.instrument_id ? [trade.instrument_id] : []);

    const instrument = trade?.instrument;
    const instrumentId = trade?.instrument_id;
    const side = trade?.side || 'buy';
    const leverage = trade?.leverage || trade?.trading_account?.leverage || 100;

    const currentPriceData = instrumentId ? prices?.get?.(instrumentId) : null;
    const currentPrice =
        currentPriceData?.last ?? currentPriceData?.bid ?? currentPriceData?.ask ?? 0;

    const calculation = calculateMutation.data;
    const unrealizedPnl = calculation?.calculations?.unrealized_pnl;
    const isCalculating = calculateMutation.isPending;

    // Fetch when lotSize, entryPrice, or currentPrice change (no timeout, throttle only)
    useEffect(() => {
        if (!open || !trade || !instrumentId || !lotSize || !entryPrice) return;

        const lots = parseFloat(lotSize);
        const price = parseFloat(entryPrice);
        if (isNaN(lots) || lots < MIN_LOT || isNaN(price) || price <= 0) return;

        const priceToUse = currentPrice > 0 ? currentPrice : price;
        if (
            prevValuesRef.current?.lotSize === lotSize &&
            prevValuesRef.current?.entryPrice === entryPrice &&
            prevValuesRef.current?.currentPrice === priceToUse
        ) {
            return; // No meaningful change
        }

        prevValuesRef.current = { lotSize, entryPrice, currentPrice: priceToUse };
        calculateMutation.mutate({
            instrument_id: instrumentId,
            lot_size: lots,
            leverage,
            price,
            entry_price: price,
            current_price: priceToUse,
            side: side as 'buy' | 'sell',
        });
    }, [lotSize, entryPrice, currentPrice]);

    useEffect(() => {
        if (open && trade) {
            setLotSize(String(trade.lot_size ?? ''));
            setEntryPrice(String(trade.entry_price ?? ''));
            prevValuesRef.current = null;
            calculateMutation.reset();
        }
    }, [open, trade?.id]);

    const handleSave = async () => {
        if (!trade) return;
        const lots = parseFloat(lotSize);
        const price = parseFloat(entryPrice);
        if (isNaN(lots) || lots < MIN_LOT || lots > MAX_LOT) {
            return;
        }
        if (isNaN(price) || price <= 0) {
            return;
        }
        setIsSaving(true);
        try {
            await onSave({ lot_size: lots, price });
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const pnlNumber = typeof unrealizedPnl === 'number' ? unrealizedPnl : 0;
    const isProfit = pnlNumber > 0;
    const pnlColor = isProfit
        ? 'text-green-600 dark:text-green-400'
        : pnlNumber < 0
          ? 'text-red-600 dark:text-red-400'
          : 'text-muted-foreground';

    const minLot = (instrument as any)?.min_lot ?? MIN_LOT;
    const maxLot = (instrument as any)?.max_lot ?? MAX_LOT;

    if (!trade) return null;

    return (
        <Dialog open={open} onOpenChange={open => !open && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Edit Trade
                        {instrument?.symbol && (
                            <Badge
                                variant={side === 'buy' ? 'default' : 'secondary'}
                                className="gap-1"
                            >
                                {side === 'buy' ? (
                                    <TrendingUp className="h-3 w-3" />
                                ) : (
                                    <TrendingDown className="h-3 w-3" />
                                )}
                                {instrument.symbol}
                            </Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div>
                        <Label className="text-sm text-muted-foreground">Current Price</Label>
                        <div className="mt-1 font-mono text-lg font-medium">
                            {instrument ? (
                                <RealTimePrice
                                    instrument={instrument as any}
                                    fallbackPrice={currentPrice || Number(entryPrice) || 0}
                                    className="font-mono"
                                />
                            ) : (
                                <span className="text-muted-foreground">-</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="lot-size">Lot Size</Label>
                        <Input
                            id="lot-size"
                            type="number"
                            step="0.01"
                            min={minLot}
                            max={maxLot}
                            value={lotSize}
                            onChange={e => setLotSize(e.target.value)}
                            placeholder={`${minLot} - ${maxLot}`}
                            className="mt-1 font-mono"
                        />
                    </div>
                    <div>
                        <Label htmlFor="entry-price">Entry Price</Label>
                        <Input
                            id="entry-price"
                            type="number"
                            step="0.00001"
                            min="0"
                            value={entryPrice}
                            onChange={e => setEntryPrice(e.target.value)}
                            placeholder="0.00000"
                            className="mt-1 font-mono"
                        />
                    </div>
                    {(unrealizedPnl !== undefined || isCalculating) && (
                        <div className="rounded-lg border bg-muted/30 p-3">
                            <div className="text-sm text-muted-foreground">Estimated P&L</div>
                            <div className={`mt-1 font-mono text-lg font-semibold ${pnlColor}`}>
                                {isCalculating && unrealizedPnl === undefined ? (
                                    <span className="text-muted-foreground">Calculating...</span>
                                ) : (
                                    <>
                                        {isProfit ? (
                                            <TrendingUp className="mr-1 inline h-4 w-4" />
                                        ) : pnlNumber < 0 ? (
                                            <TrendingDown className="mr-1 inline h-4 w-4" />
                                        ) : null}
                                        {pnlNumber >= 0 ? '+' : ''}${formatAmount(pnlNumber, 2)}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
